<template>
  <div class="panel panel-default">

    <!-- Correct answer banner -->
    <transition name="fade">
      <div v-if="correctAnswer" class="feedback feedback-correct text-center">
        <span class="feedback-text">✓ Correct! Well done!</span>
        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
        </div>
      </div>
    </transition>

    <!-- Wrong answer banner -->
    <transition name="fade">
      <div v-if="wrongAnswer" class="feedback feedback-wrong text-center">
        <span class="feedback-text">✗ Wrong! Try again.</span>
      </div>
    </transition>

    <!-- Question display -->
    <div class="panel-heading">
      <!-- Plain-text question for basics / improper / mixed -->
      <h3 v-if="questionText" class="panel-title text-center question-text">
        {{ questionText }}
      </h3>

      <!-- Arithmetic question rendered with visual fractions -->
      <div v-if="arithmeticParts" class="arith-question text-center">
        <span class="arith-label">Calculate:</span>
        <div class="arith-expr">
          <frac-display :num="arithmeticParts.n1" :den="arithmeticParts.d1" />
          <span class="arith-op">{{ arithmeticParts.op }}</span>
          <frac-display :num="arithmeticParts.n2" :den="arithmeticParts.d2" />
          <span class="arith-op">=</span>
          <span class="arith-blank">?</span>
        </div>
      </div>
    </div>

    <!-- Answer buttons -->
    <div class="panel-body answer-grid">
      <button
        v-for="(btn, index) in btnData"
        :key="index"
        class="ans-btn"
        :disabled="correctAnswer"
        @click="onAnswer(btn.correct)"
      >
        <!-- Fraction buttons rendered visually when they contain "/" -->
        <template v-if="btn.isFrac">
          <frac-display :num="btn.fracNum" :den="btn.fracDen" />
        </template>
        <!-- Mixed number buttons e.g. "3 1/4" -->
        <template v-else-if="btn.isMixed">
          <span class="mixed-whole">{{ btn.whole }}</span>
          <frac-display :num="btn.mNum" :den="btn.mDen" />
        </template>
        <template v-else>
          {{ btn.label }}
        </template>
      </button>
    </div>

    <!-- Next Question -->
    <div class="text-center" style="margin-top: 16px; margin-bottom: 8px;">
      <button class="next-btn" @click="nextQuestion">Next Question →</button>
    </div>
  </div>
</template>

<script>
// ─── Inline sub-component: renders a visual stacked fraction ───────────────
const FracDisplay = {
  functional: true,
  props: ["num", "den"],
  render(h, ctx) {
    const { num, den } = ctx.props;
    return h("span", { class: "frac-display" }, [
      h("span", { class: "frac-num" }, [String(num)]),
      h("span", { class: "frac-bar" }),
      h("span", { class: "frac-den" }, [String(den)]),
    ]);
  },
};

// ─── Math helpers ──────────────────────────────────────────────────────────
function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  return b === 0 ? a : gcd(b, a % b);
}

function lcm(a, b) {
  return (a * b) / gcd(a, b);
}

function simplify(num, den) {
  const g = gcd(Math.abs(num), Math.abs(den));
  return { num: num / g, den: den / g };
}

// "3/4"  →  { num:3, den:4 }
function fracLabel(num, den) {
  return `${num}/${den}`;
}

// mixed: whole=2, num=1, den=4  →  "2 1/4"
function mixedLabel(whole, num, den) {
  return `${whole} ${num}/${den}`;
}

// Parse a label back so we can build btn objects
function parseFrac(label) {
  const parts = label.split("/");
  return { num: parseInt(parts[0]), den: parseInt(parts[1]) };
}

function parseMixed(label) {
  // "2 1/4"
  const [wholeStr, fracStr] = label.split(" ");
  const [numStr, denStr] = fracStr.split("/");
  return { whole: parseInt(wholeStr), mNum: parseInt(numStr), mDen: parseInt(denStr) };
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Build a button object from a fraction label string
function fracBtn(label, correct) {
  const { num, den } = parseFrac(label);
  return { label, correct, isFrac: true, isMixed: false, fracNum: num, fracDen: den };
}

// Build a button object from a mixed-number label string
function mixedBtn(label, correct) {
  const { whole, mNum, mDen } = parseMixed(label);
  return { label, correct, isFrac: false, isMixed: true, whole, mNum, mDen };
}

// Plain text button
function plainBtn(label, correct) {
  return { label, correct, isFrac: false, isMixed: false };
}

// ─── Question generators ───────────────────────────────────────────────────

function makeBasicsQuestion() {
  const den1 = randInt(2, 9);
  const num1 = randInt(1, den1 - 1);
  const den2 = randInt(2, 9);
  const num2 = randInt(1, den2 - 1);

  const type = ["compare", "simplify", "equivalent"][randInt(0, 2)];

  if (type === "compare") {
    const val1 = num1 / den1;
    const val2 = num2 / den2;
    const correctLabel = val1 >= val2 ? fracLabel(num1, den1) : fracLabel(num2, den2);
    const wrongLabel   = val1 >= val2 ? fracLabel(num2, den2) : fracLabel(num1, den1);
    const e1 = fracLabel(randInt(1, 5), randInt(2, 9));
    const e2 = fracLabel(randInt(1, 5), randInt(2, 9));
    return {
      text: `Which fraction is larger: ${fracLabel(num1, den1)} or ${fracLabel(num2, den2)}?`,
      arith: null,
      buttons: shuffle([
        fracBtn(correctLabel, true),
        fracBtn(wrongLabel,   false),
        fracBtn(e1,           false),
        fracBtn(e2,           false),
      ]),
    };
  }

  if (type === "simplify") {
    const factor = randInt(2, 5);
    const bigNum = num1 * factor;
    const bigDen = den1 * factor;
    const { num: sn, den: sd } = simplify(bigNum, bigDen);
    const correct = fracLabel(sn, sd);
    const options = [fracBtn(correct, true)];
    while (options.length < 4) {
      const wn = randInt(1, 8), wd = randInt(2, 9);
      const { num: wsn, den: wsd } = simplify(wn, wd);
      const wl = fracLabel(wsn, wsd);
      if (!options.find(o => o.label === wl)) options.push(fracBtn(wl, false));
    }
    return {
      text: `Simplify ${fracLabel(bigNum, bigDen)}:`,
      arith: null,
      buttons: shuffle(options),
    };
  }

  // equivalent
  const factor = randInt(2, 5);
  const eqNum = num1 * factor;
  const eqDen = den1 * factor;
  const correct = fracLabel(eqNum, eqDen);
  const options = [fracBtn(correct, true)];
  while (options.length < 4) {
    const wn = num1 * randInt(2, 5) + (Math.random() > 0.5 ? 1 : -1);
    const wd = den1 * randInt(2, 5);
    if (wn > 0 && wd > 0) {
      const wl = fracLabel(wn, wd);
      if (!options.find(o => o.label === wl)) options.push(fracBtn(wl, false));
    }
  }
  return {
    text: `Which fraction is equivalent to ${fracLabel(num1, den1)}?`,
    arith: null,
    buttons: shuffle(options),
  };
}

function makeImproperQuestion() {
  // Pick a random proper fraction and a random whole part to build an improper fraction
  const den  = randInt(2, 9);
  const whole = randInt(1, 5);
  // improper numerator = whole * den + remainder
  const remainder = randInt(1, den - 1);
  const impNum = whole * den + remainder;

  const types = ["identify", "convert_to_mixed", "convert_to_improper"];
  const type  = types[randInt(0, 2)];

  if (type === "identify") {
    // "Which of these is an improper fraction?"
    const correct = fracLabel(impNum, den);
    // distractors: proper fractions
    const options = [fracBtn(correct, true)];
    while (options.length < 4) {
      const pn = randInt(1, 8), pd = randInt(pn + 1, 12);
      const pl = fracLabel(pn, pd);
      if (!options.find(o => o.label === pl)) options.push(fracBtn(pl, false));
    }
    return {
      text: "Which of these is an improper fraction?",
      arith: null,
      buttons: shuffle(options),
    };
  }

  if (type === "convert_to_mixed") {
    // "Convert 11/4 to a mixed number"
    const correctLabel = mixedLabel(whole, remainder, den);
    const options = [mixedBtn(correctLabel, true)];
    while (options.length < 4) {
      const fw = randInt(1, 5);
      const fn = randInt(1, den - 1);
      const wl = mixedLabel(fw, fn, den);
      if (!options.find(o => o.label === wl)) options.push(mixedBtn(wl, false));
    }
    return {
      text: `Convert ${fracLabel(impNum, den)} to a mixed number:`,
      arith: null,
      buttons: shuffle(options),
    };
  }

  // convert_to_improper: given mixed number, find the improper fraction
  const correct = fracLabel(impNum, den);
  const options = [fracBtn(correct, true)];
  while (options.length < 4) {
    const fw = randInt(1, 5), fn = randInt(1, den - 1);
    const wImpNum = fw * den + fn;
    const wl = fracLabel(wImpNum, den);
    if (!options.find(o => o.label === wl)) options.push(fracBtn(wl, false));
  }
  return {
    text: `Convert the mixed number ${mixedLabel(whole, remainder, den)} to an improper fraction:`,
    arith: null,
    buttons: shuffle(options),
  };
}

function makeMixedQuestion() {
  const den    = randInt(2, 9);
  const whole  = randInt(1, 6);
  const remainder = randInt(1, den - 1);
  const impNum = whole * den + remainder;

  const toMixed = Math.random() < 0.5;

  if (toMixed) {
    // improper → mixed
    const correct = mixedLabel(whole, remainder, den);
    const options = [mixedBtn(correct, true)];
    while (options.length < 4) {
      const fw = randInt(1, 6);
      const fn = randInt(1, den - 1);
      const wl = mixedLabel(fw, fn, den);
      if (!options.find(o => o.label === wl)) options.push(mixedBtn(wl, false));
    }
    return {
      text: `Write ${fracLabel(impNum, den)} as a mixed number:`,
      arith: null,
      buttons: shuffle(options),
    };
  }

  // mixed → improper
  const correct = fracLabel(impNum, den);
  const options = [fracBtn(correct, true)];
  while (options.length < 4) {
    const fw = randInt(1, 6), fn = randInt(1, den - 1);
    const wl = fracLabel(fw * den + fn, den);
    if (!options.find(o => o.label === wl)) options.push(fracBtn(wl, false));
  }
  return {
    text: `Write the mixed number ${mixedLabel(whole, remainder, den)} as an improper fraction:`,
    arith: null,
    buttons: shuffle(options),
  };
}

function makeArithmeticQuestion() {
  const op = ["+", "−", "×"][randInt(0, 2)];

  const den1 = randInt(2, 8);
  const num1 = randInt(1, den1 - 1);
  const den2 = randInt(2, 8);
  const num2 = randInt(1, den2 - 1);

  let resNum, resDen;

  if (op === "+") {
    const commonDen = lcm(den1, den2);
    resNum = (num1 * (commonDen / den1)) + (num2 * (commonDen / den2));
    resDen = commonDen;
  } else if (op === "−") {
    const commonDen = lcm(den1, den2);
    resNum = (num1 * (commonDen / den1)) - (num2 * (commonDen / den2));
    resDen = commonDen;
    // ensure positive result by swapping if needed
    if (resNum < 0) {
      resNum = Math.abs(resNum);
    }
  } else {
    // multiply
    resNum = num1 * num2;
    resDen = den1 * den2;
  }

  const { num: sn, den: sd } = simplify(resNum, resDen);
  const correct = fracLabel(sn, sd);
  const options = [fracBtn(correct, true)];

  while (options.length < 4) {
    // generate plausible wrong answers near the correct vicinity
    const offset = randInt(1, 3) * (Math.random() > 0.5 ? 1 : -1);
    const wn = Math.max(1, sn + offset);
    const wd = sd === 1 ? randInt(2, 8) : sd;
    const { num: wsn, den: wsd } = simplify(wn, wd);
    const wl = fracLabel(wsn, wsd);
    if (wl !== correct && !options.find(o => o.label === wl)) {
      options.push(fracBtn(wl, false));
    }
  }

  return {
    text: null,
    arith: { n1: num1, d1: den1, op, n2: num2, d2: den2 },
    buttons: shuffle(options),
  };
}

// ─── Component ────────────────────────────────────────────────────────────
export default {
  components: { FracDisplay },

  props: {
    mode: {
      type: String,
      default: "basics",
    },
  },

  data() {
    return {
      correctAnswer: false,
      wrongAnswer:   false,
      progressWidth: 0,
      questionText:  "",
      arithmeticParts: null,
      btnData: [],
      _progressTimer: null,
      _nextTimer:     null,
    };
  },

  methods: {
    generateQuestion() {
      this.correctAnswer   = false;
      this.wrongAnswer     = false;
      this.progressWidth   = 0;
      this.arithmeticParts = null;
      this.questionText    = "";
      clearTimeout(this._nextTimer);
      clearInterval(this._progressTimer);

      let q;
      switch (this.mode) {
        case "improper":   q = makeImproperQuestion();   break;
        case "mixed":      q = makeMixedQuestion();      break;
        case "arithmetic": q = makeArithmeticQuestion(); break;
        default:           q = makeBasicsQuestion();
      }

      this.questionText    = q.text;
      this.arithmeticParts = q.arith;
      this.btnData         = q.buttons;
    },

    onAnswer(isCorrect) {
      if (isCorrect) {
        this.wrongAnswer   = false;
        this.correctAnswer = true;
        this.progressWidth = 0;
        const duration = 2000;
        const interval = 30;
        const step     = (interval / duration) * 100;
        this._progressTimer = setInterval(() => {
          this.progressWidth = Math.min(this.progressWidth + step, 100);
        }, interval);
        this._nextTimer = setTimeout(() => {
          clearInterval(this._progressTimer);
          this.generateQuestion();
        }, duration);
      } else {
        this.wrongAnswer = true;
      }
    },

    nextQuestion() {
      clearInterval(this._progressTimer);
      clearTimeout(this._nextTimer);
      this.generateQuestion();
    },
  },

  created() {
    this.generateQuestion();
  },
};
</script>

<style scoped>
/* ── Feedback banners ─────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 17px;
  font-weight: bold;
}

.feedback-correct {
  background: rgba(3, 120, 60, 0.12);
  color: rgb(3, 100, 50);
  border: 2px solid rgba(3, 120, 60, 0.3);
  flex-direction: column;
  gap: 10px;
}

.progress-bar-wrap {
  width: 100%;
  height: 6px;
  background: rgba(3, 120, 60, 0.2);
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: rgb(3, 120, 60);
  border-radius: 4px;
  transition: width 30ms linear;
}

.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 2px solid rgba(180, 30, 30, 0.3);
}

/* ── Question text ────────────────────────────────────────────────── */
.question-text {
  font-size: 20px;
  color: rgb(3, 53, 48);
  padding: 16px 12px 8px;
  line-height: 1.5;
}

/* ── Arithmetic expression layout ────────────────────────────────── */
.arith-question {
  padding: 16px 12px 8px;
}
.arith-label {
  display: block;
  font-size: 15px;
  color: #555;
  margin-bottom: 12px;
}
.arith-expr {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}
.arith-op {
  font-size: 26px;
  font-weight: bold;
  color: rgb(3, 53, 48);
  line-height: 1;
}
.arith-blank {
  font-size: 26px;
  font-weight: bold;
  color: #aaa;
  border-bottom: 3px solid #aaa;
  padding: 0 8px;
}

/* ── Visual fraction (frac-display) ──────────────────────────────── */
/* Applied globally because FracDisplay is a functional component */
</style>

<!-- Non-scoped so FracDisplay renders correctly everywhere in this file -->
<style>
.frac-display {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
  gap: 1px;
}
.frac-num,
.frac-den {
  font-size: 18px;
  font-weight: bold;
  color: rgb(3, 53, 48);
  line-height: 1.1;
}
.frac-bar {
  display: block;
  width: 100%;
  min-width: 24px;
  height: 2px;
  background: rgb(3, 53, 48);
  border-radius: 2px;
}

/* ── Answer button grid ───────────────────────────────────────────── */
.answer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px;
}
.ans-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 68px;
  padding: 10px 16px;
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 2px solid rgb(3, 53, 48);
  border-radius: 8px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}
.ans-btn:hover:not(:disabled) {
  background: rgb(100, 225, 205);
  transform: scale(1.03);
}
.ans-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

/* mixed number inside a button */
.mixed-whole {
  font-size: 20px;
  font-weight: bold;
  color: rgb(3, 53, 48);
  margin-right: 4px;
}

/* ── Next button ──────────────────────────────────────────────────── */
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}
.next-btn:hover { background-color: rgb(5, 80, 70); }
</style>
